var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Card, CardContent, CircularProgress, Container, Divider, Stack, TextField, ThemeProvider, Typography, } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import Heading from "./Heading";
import robotImage from "./img/robot-512.jpg";
import Footer from "./Footer";
import getTheme from "./Theme";
import React, { useEffect, useRef, useState } from "react";
import { restoreSession, useProfileAuthStore } from "./store/ProfileStore";
import { useNavigate } from "react-router-dom";
import { useCacheBusterStore } from "./util/UrlUtils";
import { useLogger } from "./logger";
import { TopButtonMenu } from "./components/TopButtonMenu";
import { useChatStore } from "./store/ChatStore";
import { saveLoginRedirect } from "./Auth/AuthUtils";
const LOGPREFIX = "Chat: ";
const THINKING_SAYINGS = [
    "Still working on it... good things take time!",
    "Thinking extra hard on this one...",
    "Almost there, just polishing my thoughts...",
    "Give me a moment, this is a tricky one...",
    "Brewing up a great response...",
    "Deep in thought... don't go anywhere!",
    "Hang tight, almost done...",
    "Hmm, let me think about this a bit more...",
    "Bear with me, I want to get this right!",
    "One moment, I'm onto something good...",
    "Let me gather my thoughts...",
    "This one deserves a thoughtful answer...",
    "Working on it, sit tight!",
    "Just a bit longer, I promise!",
    "Putting some extra thought into this...",
    "Hmm, interesting question...",
    "Still here, just thinking it through!",
    "Want to make sure I get this right...",
    "Good question, let me think...",
];
const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    else {
        return (date.toLocaleDateString() +
            " " +
            date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    }
};
const MessageBubble = ({ message }) => {
    const isBot = message.source === "bot";
    return (_jsx(Stack, Object.assign({ direction: "row", sx: {
            justifyContent: isBot ? "flex-start" : "flex-end",
            marginBottom: "8px",
            paddingX: "10px",
        } }, { children: _jsxs(Stack, Object.assign({ direction: isBot ? "row" : "row-reverse", spacing: 1, sx: { maxWidth: "95%" } }, { children: [_jsx(Stack, Object.assign({ sx: {
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: isBot ? "#E3F2FD" : "#E8F5E8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: "4px",
                    } }, { children: isBot ? (_jsx(SmartToyIcon, { sx: { fontSize: "18px", color: "#1976D2" } })) : (_jsx(PersonIcon, { sx: { fontSize: "18px", color: "#2E7D32" } })) })), _jsx(Card, Object.assign({ "data-testid": "message-bubble", sx: {
                        backgroundColor: isBot ? "#F5F5F5" : "#E3F2FD",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    } }, { children: _jsxs(CardContent, Object.assign({ sx: { padding: "8px 12px !important" } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", sx: {
                                    wordBreak: "break-word",
                                    whiteSpace: "pre-wrap", // Preserve newlines and whitespace
                                } }, { children: message.messageText })), _jsx(Typography, Object.assign({ variant: "caption", sx: {
                                    color: "text.secondary",
                                    fontSize: "0.7rem",
                                    marginTop: "4px",
                                    display: "block",
                                } }, { children: formatMessageTime(message.timestamp) }))] })) }))] })) })));
};
export const Chat = () => {
    const [sessionId, setSessionId] = useState("");
    const [messageText, setMessageText] = useState("");
    const [polling, setPolling] = useState(false);
    const [pollingTimeout, setPollingTimeout] = useState(null);
    const [thinkingMessage, setThinkingMessage] = useState(null);
    const messagesEndRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const messageInputRef = useRef(null);
    const thinkingTimerRef = useRef(null);
    const auth = useProfileAuthStore((state) => state);
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const navigate = useNavigate();
    const logger = useLogger((state) => state);
    const chatStore = useChatStore();
    // Handle ResizeObserver errors (common with Material UI and images)
    useEffect(() => {
        const handleResizeObserverError = (event) => {
            if (event.message &&
                event.message.includes("ResizeObserver loop completed with undelivered notifications")) {
                event.preventDefault();
                event.stopPropagation();
                console.clear(); // Clear the console error
                return false;
            }
            if (event.error &&
                event.error.message &&
                event.error.message.includes("ResizeObserver loop completed with undelivered notifications")) {
                event.preventDefault();
                event.stopPropagation();
                console.clear(); // Clear the console error
                return false;
            }
        };
        const handleUnhandledRejection = (event) => {
            if (event.reason &&
                typeof event.reason === "string" &&
                event.reason.includes("ResizeObserver loop completed with undelivered notifications")) {
                event.preventDefault();
                console.clear(); // Clear the console error
                return false;
            }
        };
        // Add both error and unhandledrejection listeners
        window.addEventListener("error", handleResizeObserverError, true);
        window.addEventListener("unhandledrejection", handleUnhandledRejection, true);
        // Also suppress the specific ResizeObserver error globally
        const originalConsoleError = console.error;
        console.error = (...args) => {
            const message = args.join(" ");
            if (message.includes("ResizeObserver loop completed with undelivered notifications")) {
                return; // Suppress this specific error
            }
            originalConsoleError.apply(console, args);
        };
        // Additional global error handler for webpack dev server overlay
        const originalOnError = window.onerror;
        window.onerror = (message, source, lineno, colno, error) => {
            if (typeof message === "string" &&
                message.includes("ResizeObserver loop completed with undelivered notifications")) {
                return true; // Prevent the error from showing
            }
            if (originalOnError) {
                return originalOnError(message, source, lineno, colno, error);
            }
            return false;
        };
        return () => {
            window.removeEventListener("error", handleResizeObserverError, true);
            window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
            console.error = originalConsoleError; // Restore original console.error
            window.onerror = originalOnError; // Restore original window.onerror
        };
    }, []);
    // Generate session ID on mount
    useEffect(() => {
        const newSessionId = crypto.randomUUID();
        setSessionId(newSessionId);
        chatStore.setSessionId(newSessionId);
        logger.log(LOGPREFIX + "Generated session ID: " + newSessionId);
    }, []);
    // Authentication check
    useEffect(() => {
        const userId = auth.userId;
        const isSignedIn = auth.isSignedIn();
        if (!isSignedIn || !userId) {
            if (!restoreSession(auth, logger)) {
                logger.log(LOGPREFIX + "User NOT logged in - saving redirect and navigating...");
                // Save the current URL before redirecting to welcome page for post-login redirect
                saveLoginRedirect(window.location.href);
                navigate("/Welcome", { replace: true });
                return;
            }
        }
    }, [auth.userId, navigate, logger]);
    // Load initial messages when session ID is ready
    useEffect(() => {
        const userId = auth.userId;
        if (sessionId && userId) {
            logger.log(LOGPREFIX + "Loading initial messages for session: " + sessionId);
            chatStore
                .getMessages(sessionId, auth, cacheBusterStore, logger)
                .catch((error) => {
                logger.error(LOGPREFIX + "Error loading initial messages: " + error.message);
            });
        }
    }, [sessionId, auth.userId]);
    // Auto-scroll to bottom when messages change
    useEffect(() => {
        var _a;
        (_a = messagesEndRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: "smooth" });
    }, [chatStore.messages]);
    // Restore focus when messages change (especially when bot responds)
    useEffect(() => {
        if (chatStore.messages.length > 0) {
            setTimeout(() => {
                var _a;
                (_a = messageInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 300);
        }
    }, [chatStore.messages.length]);
    // Rotate thinking messages while polling
    useEffect(() => {
        if (polling) {
            let lastIndex = -1;
            thinkingTimerRef.current = setInterval(() => {
                let newIndex;
                do {
                    newIndex = Math.floor(Math.random() * THINKING_SAYINGS.length);
                } while (newIndex === lastIndex && THINKING_SAYINGS.length > 1);
                lastIndex = newIndex;
                setThinkingMessage(THINKING_SAYINGS[newIndex]);
            }, 10000);
        }
        else {
            if (thinkingTimerRef.current) {
                clearInterval(thinkingTimerRef.current);
                thinkingTimerRef.current = null;
            }
            setThinkingMessage(null);
        }
        return () => {
            if (thinkingTimerRef.current) {
                clearInterval(thinkingTimerRef.current);
                thinkingTimerRef.current = null;
            }
        };
    }, [polling]);
    // Clean up polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            if (pollingTimeout) {
                clearTimeout(pollingTimeout);
                setPollingTimeout(null);
            }
        };
    }, []); // Remove pollingTimeout dependency to prevent race condition
    const startPolling = () => {
        // Clean up any existing polling
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (pollingTimeout) {
            clearTimeout(pollingTimeout);
            setPollingTimeout(null);
        }
        setPolling(true);
        logger.log(LOGPREFIX + "Starting polling for new messages");
        let pollCount = 0;
        let isPolling = false; // Prevent concurrent polling calls
        const maxPolls = 90; // 90 seconds max (90 polls * 1 second each)
        const pollingStartTime = Date.now();
        const pollForMessages = () => __awaiter(void 0, void 0, void 0, function* () {
            // Skip if already polling to prevent sequence number conflicts
            if (isPolling) {
                logger.log(LOGPREFIX + "Skipping poll - previous request still in progress");
                return;
            }
            isPolling = true;
            try {
                pollCount++;
                logger.log(LOGPREFIX + `Polling attempt ${pollCount}/${maxPolls}`);
                // Get fresh auth state for each polling request to ensure updated bearer/sequence
                const freshAuth = useProfileAuthStore.getState();
                const messages = yield chatStore.getMessages(sessionId, freshAuth, cacheBusterStore, logger);
                // Check if we have messages and if the most recent message is from the bot
                if (messages.length > 0) {
                    // Sort messages by timestamp to get the most recent
                    const sortedMessages = [...messages].sort((a, b) => b.timestamp - a.timestamp);
                    const mostRecentMessage = sortedMessages[0];
                    // Check if the most recent message is from the bot and occurred after polling started
                    if (mostRecentMessage.source === "bot" &&
                        mostRecentMessage.timestamp > pollingStartTime) {
                        logger.log(LOGPREFIX + "Bot responded with new message, stopping polling");
                        stopPolling();
                        return;
                    }
                }
                // Stop polling after max attempts
                if (pollCount >= maxPolls) {
                    logger.log(LOGPREFIX +
                        "Polling timeout reached after " +
                        maxPolls +
                        " seconds");
                    stopPolling();
                    return;
                }
            }
            catch (error) {
                logger.error(LOGPREFIX + "Polling error: " + error.message);
                // Check if this is a server-side error that should stop polling
                const errorMessage = error.message;
                if (errorMessage.includes("HTTP 4") ||
                    errorMessage.includes("HTTP 5") ||
                    errorMessage.includes("78007") ||
                    errorMessage.includes("UNEXPECTED_ERROR")) {
                    logger.log(LOGPREFIX + "Polling stopped due to server error: " + errorMessage);
                    stopPolling();
                    return;
                }
                // Continue polling on transient errors, but count them towards the limit
                if (pollCount >= maxPolls) {
                    logger.log(LOGPREFIX + "Polling stopped due to repeated errors");
                    stopPolling();
                    return;
                }
            }
            finally {
                isPolling = false; // Always reset the flag
            }
        });
        // Start polling immediately, then every second
        pollForMessages();
        pollingIntervalRef.current = setInterval(pollForMessages, 1000);
        // Set overall timeout as a safety net
        const timeout = setTimeout(() => {
            logger.log(LOGPREFIX + "Overall polling timeout, stopping");
            stopPolling();
        }, maxPolls * 1000 + 5000); // Add 5 second buffer
        setPollingTimeout(timeout);
    };
    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (pollingTimeout) {
            clearTimeout(pollingTimeout);
            setPollingTimeout(null);
        }
        setPolling(false);
        logger.log(LOGPREFIX + "Polling stopped");
        // Restore focus to input field after bot responds
        setTimeout(() => {
            var _a;
            (_a = messageInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }, 200);
    };
    const handleSendMessage = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!messageText.trim()) {
            return;
        }
        const message = messageText.trim();
        setMessageText("");
        try {
            logger.log(LOGPREFIX + "Sending message: " + message);
            yield chatStore.sendMessage(message, sessionId, auth, cacheBusterStore, logger);
            // Start polling for bot response
            startPolling();
            // Restore focus to the input field after sending
            setTimeout(() => {
                var _a;
                (_a = messageInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100);
        }
        catch (error) {
            logger.error(LOGPREFIX + "Error sending message: " + error.message);
            // Also restore focus on error so user can try again
            setTimeout(() => {
                var _a;
                (_a = messageInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100);
        }
    });
    const handleKeyPress = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };
    const content = (_jsxs(Stack, Object.assign({ sx: { padding: 0, margin: 0, marginTop: "5px" }, spacing: 2 }, { children: [_jsx(TopButtonMenu, { current: undefined }), _jsx(Typography, Object.assign({ variant: "h5", color: "primary", sx: { textAlign: "center", margin: "10px" } }, { children: "AI Chat Assistant" })), chatStore.error && (_jsx(Stack, Object.assign({ sx: { padding: "10px" } }, { children: _jsxs(Typography, Object.assign({ variant: "body2", color: "error" }, { children: ["Error: ", chatStore.error] })) }))), _jsx(Stack, Object.assign({ sx: {
                    flex: 1,
                    minHeight: "400px",
                    maxHeight: "600px",
                    overflow: "auto",
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    marginX: "10px",
                    backgroundColor: "#FAFAFA",
                } }, { children: chatStore.messages.length === 0 ? (_jsxs(Stack, Object.assign({ sx: {
                        height: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "40px",
                    } }, { children: [_jsx("img", { src: robotImage, alt: "AI Chat Robot", style: {
                                width: "120px",
                                height: "120px",
                                marginBottom: "16px",
                                opacity: 0.6, // Match the text.secondary opacity
                            } }), _jsx(Typography, Object.assign({ variant: "h6", color: "text.secondary", sx: { textAlign: "center" } }, { children: "Start a conversation" })), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary", sx: { textAlign: "center", marginTop: "8px" } }, { children: "Type a message below to begin chatting with the AI assistant" }))] }))) : (_jsxs(Stack, Object.assign({ sx: { padding: "10px 0" } }, { children: [chatStore.messages.map((message) => (_jsx(MessageBubble, { message: message }, message.messageId))), chatStore.loading && !polling && (_jsx(Stack, Object.assign({ direction: "row", sx: {
                                justifyContent: "flex-end",
                                marginBottom: "8px",
                                paddingX: "10px",
                            } }, { children: _jsxs(Stack, Object.assign({ direction: "row-reverse", spacing: 1, sx: { maxWidth: "95%" } }, { children: [_jsx(Stack, Object.assign({ sx: {
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "50%",
                                            backgroundColor: "#E8F5E8",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                            marginTop: "4px",
                                        } }, { children: _jsx(PersonIcon, { sx: { fontSize: "18px", color: "#2E7D32" } }) })), _jsx(Card, Object.assign({ sx: {
                                            backgroundColor: "#E3F2FD",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                        } }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "8px 12px !important" } }, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 1, alignItems: "center" }, { children: [_jsx(CircularProgress, { size: 16 }), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary", sx: { fontStyle: "italic" } }, { children: "Sending message..." }))] })) })) }))] })) }))), polling && (_jsx(Stack, Object.assign({ direction: "row", sx: {
                                justifyContent: "flex-start",
                                marginBottom: "8px",
                                paddingX: "10px",
                            } }, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 1, sx: { maxWidth: "95%" } }, { children: [_jsx(Stack, Object.assign({ sx: {
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "50%",
                                            backgroundColor: "#E3F2FD",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                            marginTop: "4px",
                                        } }, { children: _jsx(SmartToyIcon, { sx: { fontSize: "18px", color: "#1976D2" } }) })), _jsx(Card, Object.assign({ sx: {
                                            backgroundColor: "#F5F5F5",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                        } }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "8px 12px !important" } }, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 1, alignItems: "center" }, { children: [_jsx(CircularProgress, { size: 16 }), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary", sx: { fontStyle: "italic" } }, { children: thinkingMessage || "Bot is thinking..." }))] })) })) }))] })) }))), _jsx("div", { ref: messagesEndRef })] }))) })), _jsxs(Stack, Object.assign({ sx: { padding: "0 10px 20px 10px" } }, { children: [_jsx(Divider, { sx: { marginBottom: "16px" } }), _jsxs(Stack, Object.assign({ direction: "row", spacing: 2 }, { children: [_jsx(TextField, { fullWidth: true, multiline: true, maxRows: 4, placeholder: "Type your message here...", value: messageText, onChange: (e) => setMessageText(e.target.value), onKeyPress: handleKeyPress, disabled: chatStore.loading || polling, size: "small", inputRef: messageInputRef }), _jsx(Button, Object.assign({ variant: "contained", onClick: handleSendMessage, disabled: chatStore.loading || polling || !messageText.trim(), startIcon: chatStore.loading ? _jsx(CircularProgress, { size: 20 }) : _jsx(SendIcon, {}), sx: { minWidth: "100px" } }, { children: "Send" }))] })), _jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary", sx: { marginTop: "8px", textAlign: "center" } }, { children: "Press Enter to send, Shift+Enter for new line" }))] }))] })));
    const output = (_jsx("div", Object.assign({ style: { width: "100%", padding: 0, margin: 0 }, id: "outer", "data-testid": "chat-page" }, { children: _jsxs(ThemeProvider, Object.assign({ theme: getTheme() }, { children: [_jsx(Heading, {}), _jsx(Container, Object.assign({ sx: {
                        backgroundColor: "#FFFFFF",
                        width: "100%",
                        margin: 0,
                        padding: 0,
                    }, maxWidth: false }, { children: content })), _jsx(Footer, {})] })) })));
    return output;
};
